import {
  IonButtons,
  IonModal,
  IonToolbar,
  IonHeader,
  IonContent,
  IonGrid,
  IonCol,
  IonRow,
  IonSearchbar,
  IonRange,
  IonList,
  IonItem,
  IonCheckbox,
  IonInput,
  IonIcon,
  IonText,
} from '@ionic/react';
import {
  ComponentProps,
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { close } from 'ionicons/icons';
import Trans from 'util/Trans';
import DynamicButton from 'components/buttons/DynamicButton';
import Icon from 'components/icons/Icon';
import { continents } from 'spinnaxBase/data/locations';
import { ExploreElementType, FilterData } from '../../spinnaxBase/data/explore';
import './FilterEditModal.css';

export interface FilterEditModalProps extends ComponentProps<typeof IonModal> {
  onCommit: (filterData: FilterData) => Promise<void>; // KORRIGIERT: FilterData verwenden
  currentFilters?: FilterData; // NEU: currentFilters Property hinzufügen
}

const FilterEditModal: FunctionComponent<FilterEditModalProps> = ({
  onCommit,
  currentFilters,
  ...rest
}) => {
  const modalRef = useRef<HTMLIonModalElement>(null);

  // Erweitere die States für alle Filter-Buttons
  const [activeFilter, setActiveFilter] = useState<number>(1);

  // Separate States für Countries Filter (Filter 1) - für Standorte
  const [locationContinent, setLocationContinent] = useState<string | null>(
    null
  );
  const [locationCountry, setLocationCountry] = useState<string | null>(null);
  const [locationCity, setLocationCity] = useState<string | null>(null);
  const [locationSkatepark, setLocationSkatepark] = useState<string | null>(
    null
  );

  // Separate States für Nationality Filter (Filter 2) - für Nationalitäten
  const [nationalityContinent, setNationalityContinent] = useState<
    string | null
  >(null);
  const [nationalityCountry, setNationalityCountry] = useState<string | null>(
    null
  );

  // Legacy States für Backward Compatibility (werden basierend auf activeFilter gemappt)
  const selectedContinent =
    activeFilter === 1 ? locationContinent : nationalityContinent;
  const selectedCountry =
    activeFilter === 1 ? locationCountry : nationalityCountry;
  const selectedCity = activeFilter === 1 ? locationCity : null;
  const selectedSkatepark = activeFilter === 1 ? locationSkatepark : null;

  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [rawSearchInput, setRawSearchInput] = useState<string>('');

  // Neue States für die zwei Speicher hinzufügen
  const [firstSearchStore, setFirstSearchStore] = useState<string>('');
  const [secondSearchStore, setSecondSearchStore] = useState<string>('');

  // NEUE STATES für Filter-Daten - mit korrekten Types
  const [selectedElementTypes, setSelectedElementTypes] = useState<
    ExploreElementType[]
  >(currentFilters?.elementTypes || []);
  const [favoritesOnly, setFavoritesOnly] = useState<boolean>(
    currentFilters?.favorites || false
  );

  // KORRIGIERT: Verwende locations und nationalities aus FilterData
  const [selectedLocations, setSelectedLocations] = useState<string[]>(
    currentFilters?.locations || []
  );
  const [selectedNationalities, setSelectedNationalities] = useState<string[]>(
    currentFilters?.nationalities || []
  );

  // NEUE STATES für Gender und Age Filter
  const [selectedGenders, setSelectedGenders] = useState<string[]>(
    currentFilters?.genders || []
  );
  const [ageRange, setAgeRange] = useState(
    currentFilters?.ageRange || { min: 4, max: 80 }
  );

  // Gender Handler
  const handleGenderChange = (gender: string, checked: boolean) => {
    if (checked) {
      setSelectedGenders([...selectedGenders, gender]);
    } else {
      setSelectedGenders(selectedGenders.filter((g) => g !== gender));
    }
  };

  const dismiss = () => {
    modalRef.current?.dismiss();
  };

  // Ref für den Pills-Container
  const pillsContainerRef = useRef<HTMLDivElement>(null);

  // Neuen State für Loading hinzufügen
  const [isFilterChanging, setIsFilterChanging] = useState(false);

  // Neue States für Range-Werte hinzufügen (am Anfang der Komponente bei den anderen States)
  const [rangeValues, setRangeValues] = useState({ lower: 11, upper: 19 });

  // States für die aktuellen min/max-Grenzen des Sliders
  const [sliderMin, setSliderMin] = useState(10);
  const [sliderMax, setSliderMax] = useState(20);

  // *** WICHTIG: Synchronisation zwischen ageRange und rangeValues ***
  useEffect(() => {
    setRangeValues({ lower: ageRange.min, upper: ageRange.max });
  }, [ageRange]);

  // Event-Handler für Range-Änderungen während des Ziehens (onIonInput)
  const handleRangeInput = (e: CustomEvent) => {
    const { lower, upper } = e.detail.value;
    setRangeValues({ lower, upper });
  };

  // Event-Handler für Range-Änderungen beim Loslassen (onIonChange)
  const handleRangeChange = (e: CustomEvent) => {
    const { lower, upper } = e.detail.value;
    setRangeValues({ lower, upper });

    // *** WICHTIG: ageRange State aktualisieren! ***
    setAgeRange({ min: lower, max: upper });

    // Bereichsanpassung nur beim Loslassen
    const newMin = Math.max(
      0,
      lower <= sliderMin
        ? Math.floor(lower / 10) * 10 - 10
        : Math.floor(lower / 10) * 10
    );

    const newMax = Math.min(
      99,
      upper >= sliderMax
        ? Math.ceil(upper / 10) * 10 + 10
        : Math.ceil(upper / 10) * 10
    );

    setSliderMin(newMin);
    setSliderMax(newMax);
  };

  // ------------------------------
  // SUCHBEGRIFF-LOGIK für separate Filter
  // Handler für Kontinent-Auswahl basierend auf aktivem Filter
  const handleContinentSelect = useCallback(
    (continentName: string) => {
      // Verwende flushSync um sicherzustellen, dass State-Updates synchron sind
      flushSync(() => {
        if (activeFilter === 1) {
          // Countries Filter (Locations)
          setLocationContinent(continentName);
          setLocationCountry(null);
          setLocationCity(null);
          setLocationSkatepark(null);
        } else if (activeFilter === 2) {
          // Nationality Filter
          setNationalityContinent(continentName);
          setNationalityCountry(null);
        }
      });
      // Keine automatische Texteingabe mehr
    },
    [activeFilter]
  );

  const handleCountrySelect = useCallback(
    (countryName: string) => {
      // Verwende flushSync um sicherzustellen, dass State-Updates synchron sind
      flushSync(() => {
        if (activeFilter === 1) {
          // Countries Filter (Locations)
          setLocationCountry(countryName);
          setLocationCity(null);
          setLocationSkatepark(null);
        } else if (activeFilter === 2) {
          // Nationality Filter
          setNationalityCountry(countryName);
        }
      });
      // Keine automatische Texteingabe mehr
    },
    [activeFilter]
  );

  const handleCitySelect = useCallback(
    (cityName: string) => {
      if (activeFilter === 1) {
        // Nur bei Countries Filter verfügbar
        flushSync(() => {
          setLocationCity(cityName);
          setLocationSkatepark(null);
        });
      }
      // Keine automatische Texteingabe mehr
    },
    [activeFilter]
  );

  const handleSkateparkSelect = useCallback(
    (skateparkName: string) => {
      if (activeFilter === 1) {
        // Nur bei Countries Filter verfügbar
        flushSync(() => {
          setLocationSkatepark(skateparkName);
        });
      }
      // Keine automatische Texteingabe mehr
    },
    [activeFilter]
  );
  //---------------------------------

  // FILTERE DIE KONTINENTE, LÄNDER, STÄDTE UND SKATEPARKS NACH DEN SUCHBEGRIFFEN
  const visibleCountries = selectedContinent
    ? continents.find((c) => c.name === selectedContinent)?.countries || []
    : [];
  const visibleCities = selectedCountry
    ? visibleCountries.find((c) => c.name === selectedCountry)?.cities || []
    : [];
  const visibleSkateparks = selectedCity
    ? visibleCities.find((c) => c.name === selectedCity)?.skateparks || []
    : [];

  const filteredContinents = continents.filter(
    (continent) =>
      searchTerms.length === 0 ||
      searchTerms.some((term) =>
        continent.name.toLowerCase().includes(term.toLowerCase())
      )
  );

  const filteredCountries = visibleCountries.filter(
    (country) =>
      searchTerms.length === 0 ||
      searchTerms.some((term) =>
        country.name.toLowerCase().includes(term.toLowerCase())
      )
  );

  const filteredCities = visibleCities.filter(
    (city) =>
      searchTerms.length === 0 ||
      searchTerms.some((term) =>
        city.name.toLowerCase().includes(term.toLowerCase())
      )
  );

  const filteredSkateparks = visibleSkateparks.filter(
    (skatepark) =>
      searchTerms.length === 0 ||
      searchTerms.some((term) =>
        skatepark.name.toLowerCase().includes(term.toLowerCase())
      )
  );

  // ------------------------------

  // AUTO-SELECT-LOGIK
  // Wenn nur ein Kontinent übrig bleibt, wähle ihn automatisch aus
  // Wenn nur ein Land übrig bleibt, wähle es automatisch aus
  // Wenn nur eine Stadt übrig bleibt, wähle sie automatisch aus
  // Wenn nur ein Skatepark übrig bleibt, wähle ihn automatisch aus

  // Auto-select für Kontinente mit verbesserter State-Synchronisation
  useEffect(() => {
    if (selectedContinent === null && filteredContinents.length === 1) {
      const autoSelected = filteredContinents[0].name;
      // Verwende flushSync für synchrone State-Updates
      flushSync(() => {
        handleContinentSelect(autoSelected);
        // Wichtig: Suchbegriffe nach Auto-Select leeren, damit alle Länder angezeigt werden
        setSearchTerms([]);
        setRawSearchInput('');
      });
    }
  }, [filteredContinents, selectedContinent, handleContinentSelect]);

  // Auto-select für Länder mit Search-Term-Clearing
  useEffect(() => {
    if (
      selectedContinent !== null &&
      selectedCountry === null &&
      filteredCountries.length === 1
    ) {
      const autoSelected = filteredCountries[0].name;
      // Verwende flushSync für synchrone State-Updates
      flushSync(() => {
        handleCountrySelect(autoSelected);
        // Wichtig: Suchbegriffe nach Auto-Select leeren, damit alle Städte angezeigt werden
        setSearchTerms([]);
        setRawSearchInput('');
      });
    }
  }, [
    filteredCountries,
    selectedCountry,
    selectedContinent,
    handleCountrySelect,
  ]);

  // Auto-select für Städte mit Search-Term-Clearing
  useEffect(() => {
    if (
      activeFilter !== 2 && // Nur wenn nicht Filter 2
      selectedContinent !== null &&
      selectedCountry !== null &&
      selectedCity === null &&
      filteredCities.length === 1
    ) {
      const autoSelected = filteredCities[0].name;
      // Verwende flushSync für synchrone State-Updates
      flushSync(() => {
        handleCitySelect(autoSelected);
        // Wichtig: Suchbegriffe nach Auto-Select leeren, damit alle Skateparks angezeigt werden
        setSearchTerms([]);
        setRawSearchInput('');
      });
    }
  }, [
    filteredCities,
    selectedCity,
    selectedCountry,
    selectedContinent,
    activeFilter,
    handleCitySelect,
  ]);

  // Auto-select für Skateparks mit Search-Term-Clearing
  useEffect(() => {
    if (
      activeFilter !== 2 && // Nur wenn nicht Filter 2
      selectedContinent !== null &&
      selectedCountry !== null &&
      selectedCity !== null &&
      selectedSkatepark === null &&
      filteredSkateparks.length === 1
    ) {
      const autoSelected = filteredSkateparks[0].name;
      // Verwende flushSync für synchrone State-Updates
      flushSync(() => {
        handleSkateparkSelect(autoSelected);
        // Suchbegriffe nach Auto-Select leeren für Konsistenz
        setSearchTerms([]);
        setRawSearchInput('');
      });
    }
  }, [
    filteredSkateparks,
    selectedSkatepark,
    selectedCity,
    selectedCountry,
    selectedContinent,
    activeFilter,
    handleSkateparkSelect,
  ]);

  // ------------------------------

  // OVERFLOW-CHECK
  // Überprüfen, ob der Container für die Pillen überläuft
  const checkOverflow = (container: HTMLElement) => {
    // Gesamtbreite aller Pills
    const totalWidth = Array.from(container.children).reduce(
      (width, child) => width + (child as HTMLElement).offsetWidth,
      0
    );

    // Verfügbare Breite
    const availableWidth = container.clientWidth;

    // Erster Overflow-Check
    const hasFirstOverflow = totalWidth > availableWidth;

    if (hasFirstOverflow) {
      // Breite ohne erste Pill
      const widthWithoutFirst = Array.from(container.children)
        .slice(1)
        .reduce(
          (width, child) => width + (child as HTMLElement).offsetWidth,
          0
        );

      // Zweiter Overflow-Check
      const hasSecondOverflow = widthWithoutFirst > availableWidth;

      // Klassen setzen
      container.parentElement?.classList.add('overflow');
      container.parentElement?.classList.toggle(
        'overflow-2',
        hasSecondOverflow
      );
    } else {
      // Overflow-Klassen entfernen
      container.parentElement?.classList.remove('overflow', 'overflow-2');
    }
  };

  // Überprüfen, ob der Container überläuft, wenn sich die Filter ändern
  // und die Suchbegriffe aktualisiert werden
  useEffect(() => {
    const container = pillsContainerRef.current;
    if (container) {
      checkOverflow(container);
    }
  }, [
    selectedContinent,
    selectedCountry,
    selectedCity,
    selectedSkatepark,
    searchTerms,
    rawSearchInput, // Wichtig: rawSearchInput hinzufügen
  ]);

  // ------------------------------

  // INPUT-FUNKTION
  // Wenn der Benutzer etwas in die Suchleiste eingibt, wird der Filter zurückgesetzt
  // und die Suchbegriffe aktualisiert
  const handleInput = (e: CustomEvent) => {
    const value = e.detail.value || '';
    setRawSearchInput(value);

    const terms = value
      .split(',')
      .map((t: string) => t.trim())
      .filter(Boolean);
    setSearchTerms(terms);
  };

  // ------------------------------

  // ALLE FILTER ZURÜCKSETZEN UND SUCHLEISTE LEEREN
  const handleClearAll = () => {
    setSearchTerms([]);
    setRawSearchInput('');
    // Reset based on active filter
    if (activeFilter === 1) {
      setLocationContinent(null);
      setLocationCountry(null);
      setLocationCity(null);
      setLocationSkatepark(null);
    } else if (activeFilter === 2) {
      setNationalityContinent(null);
      setNationalityCountry(null);
    }
  };

  // FILTER-TOGGLE-FUNKTION
  const handleFilterToggle = (filterNumber: number, newState: boolean) => {
    if (newState) {
      // Loading-State setzen
      setIsFilterChanging(true);

      // Speichere die aktuelle Suche für den aktuellen Filter vor dem Wechsel
      const currentSearch = rawSearchInput;
      if (activeFilter === 1) {
        setFirstSearchStore(currentSearch);
      } else if (activeFilter === 2) {
        setSecondSearchStore(currentSearch);
      }

      // Bestimme die gespeicherte Suche für den neuen Filter
      const savedSearch =
        filterNumber === 1
          ? firstSearchStore
          : filterNumber === 2
            ? secondSearchStore
            : '';

      // Setze den neuen aktiven Filter
      setActiveFilter(filterNumber);

      // Aktualisiere die Suchleiste mit der gespeicherten Suche des neuen Filters
      setRawSearchInput(savedSearch);
      const terms = savedSearch
        .split(',')
        .map((term) => term.trim())
        .filter(Boolean);
      setSearchTerms(terms);

      // Loading-State zurücksetzen
      setIsFilterChanging(false);
    }
  };

  // KORRIGIERTE EXPORT-FUNKTION
  const handleExport = async () => {
    // Speichere letzte Suche
    if (activeFilter === 1) {
      setFirstSearchStore(rawSearchInput);
    } else if (activeFilter === 2) {
      setSecondSearchStore(rawSearchInput);
    }

    // Sammle alle ausgewählten Daten basierend auf beiden Filtern
    let additionalLocations: string[] = [];
    let additionalNationalities: string[] = [];
    let selectedContinentsList: string[] = [];

    // Countries Filter (Filter 1) - für Locations
    if (locationContinent && !locationCountry) {
      // Wenn nur Kontinent bei Countries Filter ausgewählt
      additionalLocations = [locationContinent];
      selectedContinentsList = [locationContinent];
    } else if (locationCountry) {
      // Wenn Land bei Countries Filter ausgewählt
      additionalLocations = [locationCountry];
    }

    if (locationCity) {
      additionalLocations = [...additionalLocations, locationCity];
    }

    if (locationSkatepark) {
      additionalLocations = [...additionalLocations, locationSkatepark];
    }

    // Nationality Filter (Filter 2) - für Nationalitäten
    if (nationalityContinent && !nationalityCountry) {
      // Wenn nur Kontinent bei Nationality Filter ausgewählt
      // Alle Länder dieses Kontinents als Nationalitäten hinzufügen
      const continent = continents.find((c) => c.name === nationalityContinent);
      if (continent) {
        const countryNames = continent.countries.map((country) => country.name);
        additionalNationalities = [...additionalNationalities, ...countryNames];
      }
    } else if (nationalityCountry) {
      // Wenn Land bei Nationality Filter ausgewählt
      additionalNationalities = [
        ...additionalNationalities,
        nationalityCountry,
      ];
    }

    // FilterData zusammenstellen
    const filterData: FilterData = {
      locations: [...selectedLocations, ...additionalLocations],
      nationalities: [...selectedNationalities, ...additionalNationalities],
      selectedContinents: selectedContinentsList, // Für Pills-Anzeige
      elementTypes: selectedElementTypes,
      favorites: favoritesOnly,
      searchQuery: rawSearchInput,
      genders: selectedGenders,
      ageRange: ageRange,
    };

    await onCommit(filterData);
    dismiss();
  };

  // ------------------------------

  // SUCHBEGRIFF ENTFERNEN
  // Wenn ein Suchbegriff entfernt wird, wird der Filter zurückgesetzt
  // und die anderen Filter werden aktualisiert
  const removeSearchTerm = (termToRemove: string) => {
    // 1. Aktuellen Input aus der Suchleiste parsen
    const currentInputTerms = rawSearchInput
      .split(',')
      .map((term) => term.trim())
      .filter((term) => term.length > 0);

    // 2. Index des zu entfernenden Terms finden
    const index = currentInputTerms.indexOf(termToRemove);

    // Wenn der Term nicht existiert, nichts tun
    if (index === -1) return;

    // 3. Alle Begriffe ab dem zu entfernenden löschen
    const updatedTerms = currentInputTerms.slice(0, index);

    // 4. Input-Field aktualisieren
    setRawSearchInput(
      updatedTerms.length > 0 ? updatedTerms.join(',') + ',' : ''
    );

    // 5. Filter zurücksetzen – abhängig davon, welcher Term gelöscht wurde und aktivem Filter
    if (activeFilter === 1) {
      // Countries Filter
      if (termToRemove === locationContinent) {
        setLocationContinent(null);
        setLocationCountry(null);
        setLocationCity(null);
        setLocationSkatepark(null);
      } else if (termToRemove === locationCountry) {
        setLocationCountry(null);
        setLocationCity(null);
        setLocationSkatepark(null);
      } else if (termToRemove === locationCity) {
        setLocationCity(null);
        setLocationSkatepark(null);
      } else if (termToRemove === locationSkatepark) {
        setLocationSkatepark(null);
      }
    } else if (activeFilter === 2) {
      // Nationality Filter
      if (termToRemove === nationalityContinent) {
        setNationalityContinent(null);
        setNationalityCountry(null);
      } else if (termToRemove === nationalityCountry) {
        setNationalityCountry(null);
      }
    }
  };

  return (
    <IonModal {...rest} ref={modalRef} className="edit-modal">
      <IonHeader className="ion-no-border">
        {/* <div className="modal-image-svg">
          <img src={'/assets/images/Pic_Continent_Filter.svg'} />
        </div> */}
        <IonToolbar className="edit-modal-toptoolbar">
          <IonButtons slot="start">
            <DynamicButton
              text={<Trans i18nKey="OKUpIcon" />}
              iconName="OKUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={async () => {
                handleExport();
              }}
            />
          </IonButtons>
          {/* MITTE: Drei Filter Buttons */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <IonButtons>
              <DynamicButton
                isToggleable
                text={<Trans i18nKey="FILTER1" />}
                iconName="WorldDownIcon"
                iconNameToggle="WorldUpIcon"
                buttonType="icon-full"
                size="xlarge"
                fill="clear"
                toggled={activeFilter === 1}
                onToggleChange={(newState) => {
                  handleFilterToggle(1, newState);
                }}
                onClick={() => {}} // Leerer Click-Handler
              />
            </IonButtons>
            <IonButtons>
              <DynamicButton
                isToggleable
                text={<Trans i18nKey="FILTER2" />}
                iconName="NationalityDownIcon"
                iconNameToggle="NationalityUpIcon"
                buttonType="icon-full"
                size="xlarge"
                fill="clear"
                toggled={activeFilter === 2}
                onToggleChange={(newState) => {
                  handleFilterToggle(2, newState);
                }}
                onClick={() => {}} // Leerer Click-Handler
              />
            </IonButtons>
            <IonButtons>
              <DynamicButton
                isToggleable
                text={<Trans i18nKey="FILTER3" />}
                iconName="AgeDownIcon"
                iconNameToggle="AgeUpIcon"
                buttonType="icon-full"
                size="xlarge"
                fill="clear"
                toggled={activeFilter === 3}
                onToggleChange={(newState) => {
                  handleFilterToggle(3, newState);
                }}
                onClick={() => {}} // Leerer Click-Handler
              />
            </IonButtons>
          </div>
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={dismiss}
            />
          </IonButtons>
        </IonToolbar>
        <IonToolbar
          className="edit-modal-subtoptoolbar"
          style={{
            display: activeFilter === 3 ? 'none' : 'block',
          }}
        >
          <div className="searchbar-wrapper">
            <IonSearchbar
              className="white-searchbar-ios"
              value={rawSearchInput}
              style={
                {
                  '--background': 'white',
                  '--color': 'black',
                  '--placeholder-color': '#666',
                  '--icon-color': '#666',
                  '--clear-button-color': '#666',
                } as React.CSSProperties
              }
              // Searchbar deaktivieren wenn:
              // - ein Skatepark ausgewählt ist ODER
              // - Filter 2 aktiv ist UND ein Land ausgewählt ist
              disabled={
                selectedSkatepark !== null ||
                (activeFilter === 2 && selectedCountry !== null)
              }
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  handleClearAll();
                }
              }}
              onIonInput={handleInput}
              debounce={0}
              showClearButton="never"
              onIonClear={handleClearAll}
            />
          </div>
        </IonToolbar>
      </IonHeader>

      {/* Filter Pills Section - like ExplorePage */}
      {(selectedLocations.length > 0 ||
        selectedNationalities.length > 0 ||
        selectedGenders.length > 0 ||
        selectedElementTypes.length > 0 ||
        favoritesOnly ||
        ageRange.min !== 4 ||
        ageRange.max !== 80 ||
        locationContinent ||
        locationCountry ||
        nationalityContinent ||
        nationalityCountry) && (
        <div className="filter-pills-container">
          {/* Countries Filter Pills */}
          {locationContinent && !locationCountry && (
            <span className="filter-pill">
              <Icon name="WorldIcon" size="small" />
              {locationContinent}
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setLocationContinent(null);
                  setLocationCountry(null);
                  setLocationCity(null);
                  setLocationSkatepark(null);
                  setRawSearchInput('');
                  setSearchTerms([]);
                }}
              />
            </span>
          )}

          {locationCountry && !locationCity && (
            <span className="filter-pill">
              <Icon name="WorldIcon" size="small" />
              {locationCountry}
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setLocationCountry(null);
                  setLocationCity(null);
                  setLocationSkatepark(null);
                  setRawSearchInput('');
                  setSearchTerms([]);
                }}
              />
            </span>
          )}

          {locationCity && !locationSkatepark && (
            <span className="filter-pill">
              <Icon name="WorldIcon" size="small" />
              {locationCity}
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setLocationCity(null);
                  setLocationSkatepark(null);
                  setRawSearchInput('');
                  setSearchTerms([]);
                }}
              />
            </span>
          )}

          {locationSkatepark && (
            <span className="filter-pill">
              <Icon name="WorldIcon" size="small" />
              {locationSkatepark}
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setLocationSkatepark(null);
                  setRawSearchInput('');
                  setSearchTerms([]);
                }}
              />
            </span>
          )}

          {/* Nationality Filter Pills */}
          {nationalityContinent && !nationalityCountry && (
            <span className="filter-pill">
              <Icon name="NationalityIcon" size="small" />
              {nationalityContinent}
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setNationalityContinent(null);
                  setNationalityCountry(null);
                  setRawSearchInput('');
                  setSearchTerms([]);
                }}
              />
            </span>
          )}

          {nationalityCountry && (
            <span className="filter-pill">
              <Icon name="NationalityIcon" size="small" />
              {nationalityCountry}
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setNationalityCountry(null);
                  setRawSearchInput('');
                  setSearchTerms([]);
                }}
              />
            </span>
          )}

          {/* Regular Location Pills */}
          {selectedLocations.map((location) => (
            <span key={`location-${location}`} className="filter-pill">
              <Icon name="WorldIcon" size="small" />
              {location}
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setSelectedLocations(
                    selectedLocations.filter((l) => l !== location)
                  );
                }}
              />
            </span>
          ))}

          {/* Nationality Pills */}
          {selectedNationalities.map((nationality) => (
            <span key={`nationality-${nationality}`} className="filter-pill">
              <Icon name="NationalityIcon" size="small" />
              {nationality}
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setSelectedNationalities(
                    selectedNationalities.filter((n) => n !== nationality)
                  );
                }}
              />
            </span>
          ))}

          {/* Gender Pills */}
          {selectedGenders.map((gender) => (
            <span key={`gender-${gender}`} className="filter-pill">
              <Icon name="AgeIcon" size="small" />
              {gender}
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setSelectedGenders(
                    selectedGenders.filter((g) => g !== gender)
                  );
                }}
              />
            </span>
          ))}

          {/* Age Range Pill */}
          {(ageRange.min !== 4 || ageRange.max !== 80) && (
            <span className="filter-pill">
              <Icon name="AgeIcon" size="small" />
              {ageRange.min}-{ageRange.max} Jahre
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setAgeRange({ min: 4, max: 80 });
                  setRangeValues({ lower: 4, upper: 80 });
                  setSliderMin(4);
                  setSliderMax(80);
                }}
              />
            </span>
          )}

          {/* Element Type Pills */}
          {selectedElementTypes.map((type) => (
            <span key={`type-${type}`} className="filter-pill">
              {type}
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setSelectedElementTypes(
                    selectedElementTypes.filter((t) => t !== type)
                  );
                }}
              />
            </span>
          ))}

          {/* Favorites Pill */}
          {favoritesOnly && (
            <span className="filter-pill">
              <Icon name="HeartIcon" size="small" />
              Favoriten
              <IonIcon
                icon={close}
                className="close-icon"
                onClick={() => {
                  setFavoritesOnly(false);
                }}
              />
            </span>
          )}

          {/* Clear All Button */}
          <button
            className="clear-all-pills-button"
            onClick={() => {
              // Clear all filters
              setSelectedLocations([]);
              setSelectedNationalities([]);
              setSelectedGenders([]);
              setSelectedElementTypes([]);
              setFavoritesOnly(false);
              setAgeRange({ min: 4, max: 80 });
              setRangeValues({ lower: 4, upper: 80 });
              setSliderMin(4);
              setSliderMax(80);
              // Clear search terms for both filters
              setLocationContinent(null);
              setLocationCountry(null);
              setLocationCity(null);
              setLocationSkatepark(null);
              setNationalityContinent(null);
              setNationalityCountry(null);
              setRawSearchInput('');
              setSearchTerms([]);
            }}
          >
            Alle löschen
          </button>
        </div>
      )}

      <IonContent
        style={{ '--background': 'transparent', '--border-colour': 0 }}
        scrollY={true}
      >
        {!isFilterChanging && activeFilter !== 3 ? (
          <IonGrid>
            <IonRow>
              {selectedContinent === null ? (
                filteredContinents.map((continent) => (
                  // Kontinent-Auswahl
                  <IonCol size="6" key={continent.name}>
                    <div style={{ width: '100%' }}>
                      <DynamicButton
                        style={{ borderRadius: 'var(--spinnax-border-radius)' }}
                        text={continent.name}
                        iconCode={continent.code}
                        buttonType="icon-text"
                        size="default"
                        fill="clear"
                        borderRadius="10px"
                        backgroundColor="var(--explore-element-background)"
                        textColor="white"
                        align="start"
                        expand="full"
                        onClick={() => handleContinentSelect(continent.name)}
                      />
                    </div>
                  </IonCol>
                ))
              ) : selectedCountry === null ? (
                filteredCountries.map((country) => (
                  // Länder-Auswahl
                  <IonCol size="6" key={country.name}>
                    <div style={{ width: '100%' }}>
                      <DynamicButton
                        style={{ borderRadius: 'var(--spinnax-border-radius)' }}
                        text={country.name}
                        iconCode={country.code}
                        buttonType="icon-text"
                        size="default"
                        fill="clear"
                        borderRadius="10px"
                        backgroundColor="var(--explore-element-background)"
                        textColor="white"
                        align="start"
                        expand="full"
                        onClick={() => handleCountrySelect(country.name)}
                      />
                    </div>
                  </IonCol>
                ))
              ) : activeFilter !== 2 && selectedCity === null ? (
                // Städte nur anzeigen wenn nicht Filter 2
                filteredCities.map((city) => (
                  // Städte-Auswahl
                  <IonCol size="6" key={city.name}>
                    <div style={{ width: '100%' }}>
                      <DynamicButton
                        style={{ borderRadius: 'var(--spinnax-border-radius)' }}
                        text={city.name}
                        iconName="CityIcon"
                        buttonType="icon-text"
                        size="default"
                        fill="clear"
                        borderRadius="10px"
                        backgroundColor="var(--explore-element-background)"
                        textColor="white"
                        align="start"
                        expand="full"
                        onClick={() => handleCitySelect(city.name)}
                      />
                    </div>
                  </IonCol>
                ))
              ) : activeFilter !== 2 && selectedSkatepark === null ? (
                // Skateparks nur anzeigen wenn nicht Filter 2
                filteredSkateparks.map((skatepark) => (
                  // Skateparks-Auswahl
                  <IonCol size="6" key={skatepark.name}>
                    <div style={{ width: '100%' }}>
                      <DynamicButton
                        style={{ borderRadius: 'var(--spinnax-border-radius)' }}
                        text={skatepark.name}
                        iconName="LocationIcon"
                        buttonType="icon-text"
                        size="default"
                        fill="clear"
                        borderRadius="10px"
                        backgroundColor="var(--explore-element-background)"
                        textColor="white"
                        align="start"
                        expand="full"
                        onClick={() => handleSkateparkSelect(skatepark.name)}
                      />
                    </div>
                  </IonCol>
                ))
              ) : selectedSkatepark !== null ? (
                <IonCol size="6">
                  <div style={{ width: '100%' }}>
                    <DynamicButton
                      style={{ borderRadius: 'var(--spinnax-border-radius)' }}
                      text={selectedSkatepark}
                      iconName="LocationIcon"
                      buttonType="icon-text"
                      size="default"
                      fill="clear"
                      borderRadius="10px"
                      backgroundColor="var(--explore-element-background)"
                      textColor="white"
                      align="start"
                      expand="full"
                      onClick={() => removeSearchTerm(selectedSkatepark)}
                    />
                  </div>
                </IonCol>
              ) : null}
            </IonRow>
          </IonGrid>
        ) : activeFilter === 3 ? (
          <>
            {/* GENDER SECTION */}
            <div className="edit-modal-group" style={{ padding: '0 16px', marginBottom: '16px' }}>
              <IonText>
                <Trans i18nKey="GENDER" defaults="Geschlecht" />
              </IonText>
              <div className="gender-section-innergroup">
                {['Male', 'Female', 'Diverse', 'All'].map((gender) => (
                  <div key={gender} className="gender-option">
                    <IonCheckbox
                      checked={selectedGenders.includes(gender)}
                      onIonChange={(e) =>
                        handleGenderChange(gender, e.detail.checked)
                      }
                      aria-label={`Gender selection ${gender}`}
                    />
                    <span className="gender-label">
                      {gender === 'Male'
                        ? 'Männlich'
                        : gender === 'Female'
                          ? 'Weiblich'
                          : gender === 'Diverse'
                            ? 'Divers'
                            : 'Alle'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* AGE SECTION */}
            <div style={{ margin: '0 16px' }}>
              <IonText>
                <Trans i18nKey="AGE" defaults="Alter" />
              </IonText>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div className="diamond-value">
                  <span
                    style={{
                      transform: 'rotate(-45deg)',
                      display: 'block',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    {rangeValues.lower}
                  </span>
                </div>
                <IonRange
                  aria-label="Dual Knobs Range"
                  dualKnobs={true}
                  // Feste min/max-Werte während des Ziehens
                  min={sliderMin}
                  max={sliderMax}
                  value={rangeValues}
                  onIonInput={handleRangeInput}
                  onIonChange={handleRangeChange}
                  className="diamond-knobs"
                  pin={true}
                  pinFormatter={(value: number) => `${value}`}
                />
                <div className="diamond-value">
                  <span
                    style={{
                      transform: 'rotate(-45deg)',
                      display: 'block',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    {rangeValues.upper}
                  </span>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </IonContent>
    </IonModal>
  );
};

export default FilterEditModal;
